import React from "react";
import { FaExternalLinkAlt, FaShare, FaCheck, FaFileAlt } from "react-icons/fa";
import { Chip, Divider, Tooltip, Button } from "@/components/ui";
import { BaseModal } from "@/components/ui/base-modal";
import { Content } from "@/models/document";
import HateCircular from "@/components/hate-circular";
import useTranslation from "@/hooks/use-translation";
import logger from "@/utils/logger";
import { safeString } from "@/utils/safe-value";

interface PostDetailModalProps {
  open: boolean;
  onClose: () => void;
  content: Content | null;
  sentiment?: number;
}

const PostDetailModal: React.FC<PostDetailModalProps> = ({
  open,
  onClose,
  content,
  sentiment,
}) => {
  const { t } = useTranslation();

  if (!content) return null;

  // Adapter les données comme dans NormalizeEmbed
  // Use safeString to handle cases where fields might be objects with {value: ...}
  const rawData = content as any;
  const text = safeString(rawData.description || rawData.text || rawData.title);
  const title = safeString(rawData.title) || (text ? text.substring(0, 100) + "..." : "");
  const adaptedData = rawData.normalized
    ? rawData
    : {
        id: rawData.id,
        text,
        title,
        url: safeString(rawData.url),
        date: safeString(rawData.date || rawData.published_time),
        language: safeString(rawData.lang || rawData.language),
        author: rawData.author
          ? {
              id: rawData.author.id,
              username: safeString(rawData.author.username),
              title: safeString(rawData.author.nickname || rawData.author.name),
              description: safeString(rawData.author.signature || rawData.author.bio),
              url: safeString(rawData.author.url),
              verified: rawData.author.verified,
              stats: rawData.author.stats,
              text: safeString(rawData.author.signature || rawData.author.bio),
              media: rawData.author.avatar
                ? [
                    {
                      id: "avatar",
                      type: "image",
                      url: safeString(rawData.author.avatar),
                      thumbnail: safeString(rawData.author.avatar),
                    },
                  ]
                : [],
            }
          : undefined,
        media:
          rawData.image || rawData.thumbnail
            ? [
                {
                  id: "media",
                  type: rawData.play_url ? "video" : "image",
                  url: safeString(rawData.play_url || rawData.video_url),
                  thumbnail: safeString(rawData.image || rawData.thumbnail),
                  description: safeString(rawData.description),
                },
              ]
            : [],
        stats: rawData.stats,
      };

  const handleOpenOriginal = () => {
    if (adaptedData.url) {
      window.open(adaptedData.url, "_blank");
    }
  };

  const handleShare = async () => {
    if (navigator.share && adaptedData.url) {
      try {
        await navigator.share({
          title: adaptedData.title,
          text: adaptedData.text,
          url: adaptedData.url,
        });
      } catch (error) {
        logger.error("Erreur lors du partage:", error);
      }
    } else if (adaptedData.url) {
      navigator.clipboard.writeText(adaptedData.url);
    }
  };

  const renderFooter = () => (
    <div className="flex justify-between items-center w-full">
      <div className="flex items-center gap-2">
        {sentiment && <HateCircular sentiment={sentiment} />}
      </div>
      <div className="flex gap-2">
        <Tooltip content={t("share")}>
          <Button variant="ghost" onClick={handleShare} leftIcon={<FaShare />}>
            {t("share")}
          </Button>
        </Tooltip>
        <Tooltip content={t("open_original")}>
          <Button variant="ghost" onClick={handleOpenOriginal} leftIcon={<FaExternalLinkAlt />}>
            {t("open_original")}
          </Button>
        </Tooltip>
        <Button variant="ghost" onClick={onClose}>
          {t("close") || "Fermer"}
        </Button>
      </div>
    </div>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      icon={<FaFileAlt />}
      title={t("post_details")}
      variant="accent"
      size="lg"
      customFooter={renderFooter()}
    >
      <div className="space-y-6">
        {/* Header avec auteur */}
        {adaptedData.author && (
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-arc-border flex-shrink-0">
              {adaptedData.author.media &&
                adaptedData.author.media.length > 0 && (
                  <img
                    src={adaptedData.author.media[0].url}
                    alt={adaptedData.author.username}
                    className="w-full h-full object-cover"
                  />
                )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-2">
                <h3 className="text-lg font-semibold text-content-primary">
                  {adaptedData.author.username}
                </h3>
                {adaptedData.author.title && (
                  <span className="text-sm text-content-secondary">
                    {adaptedData.author.title}
                  </span>
                )}
                {adaptedData.author.verified && (
                  <Chip
                    color="primary"
                    size="sm"
                    icon={<FaCheck className="w-2 h-2" />}
                  >
                    Verified
                  </Chip>
                )}
              </div>
              {adaptedData.author.description && (
                <p className="text-sm text-content-secondary mb-2">
                  {adaptedData.author.description}
                </p>
              )}
              {adaptedData.author.stats && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(adaptedData.author.stats).map(
                    ([key, value]) => {
                      if (value === undefined || value === null) return null;
                      return (
                        <Chip
                          key={key}
                          variant="outlined"
                          size="sm"
                        >
                          {key}: {String(value)}
                        </Chip>
                      );
                    }
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <Divider />

        {/* Contenu principal */}
        <div className="space-y-4">
          {adaptedData.title && (
            <h2 className="text-xl font-bold text-content-primary">
              {adaptedData.title}
            </h2>
          )}

          {adaptedData.text && (
            <p className="text-content-primary leading-relaxed whitespace-pre-wrap">
              {adaptedData.text}
            </p>
          )}

          {/* Média en pleine largeur */}
          {adaptedData.media && adaptedData.media.length > 0 && (
            <div className="w-full">
              <div className="relative rounded-arc-md overflow-hidden bg-surface-secondary h-64">
                {adaptedData.media.length === 1 ? (
                  <div className="w-full h-full">
                    {adaptedData.media[0].type === "image" ? (
                      <img
                        src={
                          adaptedData.media[0].url ||
                          adaptedData.media[0].thumbnail
                        }
                        alt={adaptedData.media[0].description || "Media"}
                        className="w-full h-full object-cover"
                      />
                    ) : adaptedData.media[0].type === "video" ? (
                      <video
                        src={adaptedData.media[0].url}
                        poster={adaptedData.media[0].thumbnail}
                        controls
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-surface-tertiary">
                        <span className="text-sm text-content-secondary">
                          {t("unsupported_media")}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative w-full h-full">
                    <div className="flex overflow-x-auto snap-x snap-mandatory h-full">
                      {adaptedData.media.map((media: any, index: number) => (
                        <div
                          key={index}
                          className="flex-shrink-0 w-full h-full snap-center"
                        >
                          {media.type === "image" ? (
                            <img
                              src={media.url || media.thumbnail}
                              alt={media.description || "Media"}
                              className="w-full h-full object-cover"
                            />
                          ) : media.type === "video" ? (
                            <video
                              src={media.url}
                              poster={media.thumbnail}
                              controls
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-surface-tertiary">
                              <span className="text-sm text-content-secondary">
                                {t("unsupported_media")}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {adaptedData.media.length > 1 && (
                      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                        {adaptedData.media.map((_: any, index: number) => (
                          <div
                            key={index}
                            className="w-2 h-2 bg-white rounded-full opacity-50"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <Divider />

        {/* Métadonnées détaillées */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-content-primary">
              {t("publication_info")}
            </h4>
            {adaptedData.date && (
              <div className="flex items-center space-x-2">
                <p className="text-sm text-content-secondary">
                  <strong>{t("published_on")}:</strong>{" "}
                  {new Date(adaptedData.date).toLocaleDateString("fr-FR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            )}
            {adaptedData.language && (
              <div className="flex items-center space-x-2">
                <p className="text-sm text-content-secondary">
                  <strong>{t("language")}:</strong>{" "}
                  {adaptedData.language.toUpperCase()}
                </p>
              </div>
            )}
            {adaptedData.url && (
              <div className="flex items-center space-x-2">
                <p className="text-sm text-content-secondary">
                  <strong>{t("source")}:</strong>{" "}
                  <a
                    href={adaptedData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline break-all"
                  >
                    {adaptedData.url}
                  </a>
                </p>
              </div>
            )}
          </div>

          {/* Statistiques détaillées */}
          {adaptedData.stats && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-content-primary">
                {t("statistics")}
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(adaptedData.stats).map(([key, value]) => {
                  if (value === undefined || value === null) return null;
                  return (
                    <div
                      key={key}
                      className="flex justify-between items-center p-2 bg-surface-secondary rounded-arc-sm"
                    >
                      <span className="text-sm text-content-secondary capitalize">
                        {t(key)}:
                      </span>
                      <span className="text-sm font-semibold text-content-primary">
                        {typeof value === "number"
                          ? value.toLocaleString()
                          : String(value)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Données techniques (optionnel) */}
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-content-tertiary hover:text-content-secondary">
            {t("technical_details")}
          </summary>
          <div className="mt-2 p-4 bg-surface-secondary rounded-arc-md text-xs font-mono">
            <pre className="text-content-secondary overflow-auto">
              {JSON.stringify(content, null, 2)}
            </pre>
          </div>
        </details>
      </div>
    </BaseModal>
  );
};

export default PostDetailModal;
